import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const money = (value) => Number(value || 0);
const moneyText = (value) =>
  money(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const dateText = (value) => (value ? new Date(value).toLocaleDateString("en-GB") : "");
const toBase64 = (url) =>
  fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );

function groupByFeeHead(rows) {
  return rows.reduce((groups, row) => {
    const name = row.fee_head_name || "Not Specified";
    if (!groups[name]) groups[name] = [];
    groups[name].push(row);
    return groups;
  }, {});
}

export default function FeeConcessionReport() {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [feeTypeId, setFeeTypeId] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [feeHeadId, setFeeHeadId] = useState("");
  const [feeHeads, setFeeHeads] = useState([]);
  const [collegeDetails, setCollegeDetails] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    if (!clientId || !collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/colleges/college/${clientId}/${collegeId}`)
      .then(({ data }) => {
        const college = data?.data || null;
        setCollegeDetails(college);
        if (college?.college_logopath && college?.society_id && college?.college_id) {
          const logoPath = `/uploads/logos/${clientId}/${college.society_id}/${college.college_id}/${college.college_logopath}`;
          toBase64(logoPath)
            .then(setLogoBase64)
            .catch(() => setLogoBase64(null));
        }
      })
      .catch(() => setCollegeDetails(null));
    axios
      .get(`${API_BASE_URL}/api/fee-concessions/report/filters/${clientId}/${collegeId}`)
      .then(({ data }) => {
        setAcademicYears(data?.data?.academicYears || []);
        setFeeTypes(data?.data?.feeTypes || []);
      })
      .catch(() =>
        setSnackbar({ open: true, message: "Unable to load academic years.", severity: "error" })
      );
  }, [clientId, collegeId]);

  useEffect(() => {
    setFeeTypeId("");
    if (!clientId || !collegeId) return;
    axios
      .get(`${API_BASE_URL}/api/fee-concessions/report/filters/${clientId}/${collegeId}`, {
        params: { academic_year: academicYear },
      })
      .then(({ data }) => setFeeTypes(data?.data?.feeTypes || []))
      .catch(() =>
        setSnackbar({ open: true, message: "Unable to load fee categories.", severity: "error" })
      );
  }, [academicYear, clientId, collegeId]);

  useEffect(() => {
    setFeeHeadId("");
    setFeeHeads([]);
    if (!clientId || !collegeId || !academicYear || !feeTypeId) return;
    axios
      .get(`${API_BASE_URL}/api/fee-concessions/report/filters/${clientId}/${collegeId}`, {
        params: { academic_year: academicYear, fee_type_id: feeTypeId },
      })
      .then(({ data }) => setFeeHeads(data?.data?.feeHeads || []))
      .catch(() =>
        setSnackbar({ open: true, message: "Unable to load fee heads.", severity: "error" })
      );
  }, [academicYear, feeTypeId, clientId, collegeId]);

  const grouped = useMemo(() => groupByFeeHead(rows), [rows]);
  const dateWiseRows = useMemo(() => {
    const summary = new Map();
    rows.forEach((row) => {
      const date = dateText(row.concession_date);
      const feeHead = row.fee_head_name || "Not Specified";
      const key = `${date}|${feeHead}`;
      const item = summary.get(key) || {
        id: key,
        concession_date: date,
        fee_head_name: feeHead,
        concession_entries: 0,
        total_amount: 0,
      };
      item.concession_entries += 1;
      item.total_amount += money(row.concession_amount);
      summary.set(key, item);
    });
    return Array.from(summary.values());
  }, [rows]);
  const detailTotals = useMemo(
    () => ({
      feeAmount: rows.reduce((sum, row) => sum + money(row.fee_amount), 0),
      concession: rows.reduce((sum, row) => sum + money(row.concession_amount), 0),
      paid: rows.reduce((sum, row) => sum + money(row.paid_amount), 0),
      balance: rows.reduce((sum, row) => sum + money(row.balance), 0),
    }),
    [rows]
  );

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      setSnackbar({
        open: true,
        message: "Please select From Date and To Date.",
        severity: "warning",
      });
      return;
    }
    if (fromDate > toDate) {
      setSnackbar({
        open: true,
        message: "From Date cannot be after To Date.",
        severity: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-concessions/report/${clientId}/${collegeId}`,
        {
          params: {
            from_date: fromDate,
            to_date: toDate,
            academic_year: academicYear,
            fee_type_id: feeTypeId,
            fee_head_id: feeHeadId,
          },
        }
      );
      const result = (data?.data || []).map((row) => ({
        ...row,
        id: String(row.fee_concession_id),
      }));
      setRows(result);
      if (!result.length) {
        setSnackbar({ open: true, message: "No concessions found.", severity: "info" });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Unable to fetch report.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportRows = () => {
    const selectedFeeType =
      feeTypes.find((item) => String(item.fee_type_id) === String(feeTypeId))?.fee_type_name ||
      "All";
    const selectedFeeHead =
      feeHeads.find((item) => String(item.fee_head_id) === String(feeHeadId))?.fee_head_name ||
      "All";
    const output = [
      ["FEE CONCESSION REPORT"],
      [`From Date: ${dateText(fromDate)}   To Date: ${dateText(toDate)}`],
      [
        `Academic Year: ${
          academicYear || "All"
        }   Fee Type / Category: ${selectedFeeType}   Fee Head: ${selectedFeeHead}`,
      ],
      [],
      [
        "S.No.",
        "Concession Date",
        "Student Roll No.",
        "Student Name",
        "Fee Amount",
        "Concession Amount",
        "Fee Book Concession Total",
        "Paid Amount",
        "Balance",
        "Remarks",
      ],
    ];
    let serial = 1;
    Object.keys(grouped)
      .sort()
      .forEach((feeHead) => {
        output.push([`Fee Head: ${feeHead}`]);
        grouped[feeHead].forEach((row) =>
          output.push([
            serial++,
            dateText(row.concession_date),
            row.student_rollno,
            row.student_name,
            money(row.fee_amount),
            money(row.concession_amount),
            money(row.fee_book_concession_total),
            money(row.paid_amount),
            money(row.balance),
            row.remarks || "",
          ])
        );
        output.push([
          "",
          "",
          "",
          "Fee Head Total",
          "",
          grouped[feeHead].reduce((sum, row) => sum + money(row.concession_amount), 0),
          "",
          "",
          "",
          "",
        ]);
        output.push([]);
      });
    output.push([
      "",
      "",
      "",
      "Grand Total",
      detailTotals.feeAmount,
      detailTotals.concession,
      "",
      detailTotals.paid,
      detailTotals.balance,
      "",
    ]);
    return output;
  };

  const downloadExcel = () => {
    if (!rows.length)
      return setSnackbar({ open: true, message: "No data to export.", severity: "warning" });
    const selectedFeeType =
      feeTypes.find((item) => String(item.fee_type_id) === String(feeTypeId))?.fee_type_name ||
      "All";
    const selectedFeeHead =
      feeHeads.find((item) => String(item.fee_head_id) === String(feeHeadId))?.fee_head_name ||
      "All";
    const detailSheet = XLSX.utils.aoa_to_sheet(exportRows());
    detailSheet["!cols"] = [
      { wch: 8 },
      { wch: 17 },
      { wch: 20 },
      { wch: 28 },
      { wch: 16 },
      { wch: 20 },
      { wch: 24 },
      { wch: 16 },
      { wch: 16 },
      { wch: 35 },
    ];
    const workbook = XLSX.utils.book_new();
    const dateSheet = XLSX.utils.json_to_sheet(
      dateWiseRows.map((row, index) => ({
        "S.No.": index + 1,
        "Concession Date": row.concession_date,
        "Fee Head Name": row.fee_head_name,
        "Concession Entries": row.concession_entries,
        "Total Concession Amount": row.total_amount,
      })),
      { origin: "A5" }
    );
    XLSX.utils.sheet_add_aoa(
      dateSheet,
      [
        ["FEE CONCESSION REPORT - DATE-WISE SUMMARY"],
        [`From Date: ${dateText(fromDate)}   To Date: ${dateText(toDate)}`],
        [
          `Academic Year: ${
            academicYear || "All"
          }   Fee Type / Category: ${selectedFeeType}   Fee Head: ${selectedFeeHead}`,
        ],
      ],
      { origin: "A1" }
    );
    XLSX.utils.book_append_sheet(workbook, dateSheet, "Date-wise Summary");
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Fee Concession Report");
    XLSX.writeFile(workbook, `Fee_Concession_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  const downloadPdf = () => {
    if (!rows.length)
      return setSnackbar({ open: true, message: "No data to export.", severity: "warning" });
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const selectedFeeType =
      feeTypes.find((item) => String(item.fee_type_id) === String(feeTypeId))?.fee_type_name ||
      "All";
    const selectedFeeHead =
      feeHeads.find((item) => String(item.fee_head_id) === String(feeHeadId))?.fee_head_name ||
      "All";
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, "PNG", 15, 8, 28, 28);
      } catch (error) {
        console.error("Unable to add college logo to PDF", error);
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text((collegeDetails?.college_name || "COLLEGE NAME").toUpperCase(), pageWidth / 2, 13, {
      align: "center",
    });
    if (collegeDetails?.affiliation) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.text(collegeDetails.affiliation, pageWidth / 2, 20, { align: "center" });
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("FEE CONCESSION REPORT", pageWidth / 2, 29, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `From: ${dateText(fromDate)}   To: ${dateText(toDate)}   Academic Year: ${
        academicYear || "All"
      }   Fee Type / Category: ${selectedFeeType}   Fee Head: ${selectedFeeHead}`,
      pageWidth / 2,
      36,
      { align: "center" }
    );
    doc.setFontSize(11);
    doc.text("Date-wise Concession Summary", 14, 44);
    autoTable(doc, {
      startY: 47,
      head: [["S.No.", "Concession Date", "Fee Head", "Entries", "Total Amount"]],
      body: dateWiseRows.map((row, index) => [
        index + 1,
        row.concession_date,
        row.fee_head_name,
        row.concession_entries,
        moneyText(row.total_amount),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [52, 119, 235] },
    });
    doc.addPage();
    doc.text("Fee Head-wise Date-by-Date Report", 14, 12);
    let y = 18;
    let serial = 1;
    Object.keys(grouped)
      .sort()
      .forEach((feeHead) => {
        const headRows = grouped[feeHead];
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(`Fee Head: ${feeHead}`, 14, y);
        doc.setFont(undefined, "normal");
        autoTable(doc, {
          startY: y + 3,
          head: [
            [
              "S.No.",
              "Concession Date",
              "Roll No.",
              "Student Name",
              "Fee Amount",
              "Concession",
              "Fee Book Concession Total",
              "Paid",
              "Balance",
              "Remarks",
            ],
          ],
          body: headRows.map((row) => [
            serial++,
            dateText(row.concession_date),
            row.student_rollno,
            row.student_name,
            moneyText(row.fee_amount),
            moneyText(row.concession_amount),
            moneyText(row.fee_book_concession_total),
            moneyText(row.paid_amount),
            moneyText(row.balance),
            row.remarks || "",
          ]),
          foot: [
            [
              "",
              "",
              "",
              "Fee Head Total",
              "",
              moneyText(headRows.reduce((sum, row) => sum + money(row.concession_amount), 0)),
              "",
              "",
              "",
              "",
            ],
          ],
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [52, 119, 235] },
          footStyles: { fillColor: [225, 225, 225], textColor: 20, fontStyle: "bold" },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 9;
        if (y > 180) {
          doc.addPage();
          y = 14;
        }
      });
    doc.setFont(undefined, "bold");
    const totalY = Math.min(doc.lastAutoTable.finalY + 8, 195);
    doc.text(
      `Totals - Fee Amount: Rs. ${moneyText(detailTotals.feeAmount)} | Concession: Rs. ${moneyText(
        detailTotals.concession
      )} | Paid: Rs. ${moneyText(detailTotals.paid)} | Balance: Rs. ${moneyText(
        detailTotals.balance
      )}`,
      14,
      totalY
    );
    doc.save(`Fee_Concession_Report_${fromDate}_to_${toDate}.pdf`);
  };

  const columns = [
    {
      field: "serial",
      headerName: "S.No.",
      width: 75,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "fee_head_name", headerName: "Fee Head", minWidth: 170, flex: 1 },
    {
      field: "concession_date",
      headerName: "Concession Date",
      minWidth: 140,
      valueFormatter: (params) => dateText(params.value),
    },
    { field: "student_rollno", headerName: "Student Roll No.", minWidth: 145 },
    { field: "student_name", headerName: "Student Name", minWidth: 190, flex: 1 },
    {
      field: "fee_amount",
      headerName: "Fee Amount",
      minWidth: 125,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
    {
      field: "concession_amount",
      headerName: "Concession Amount",
      minWidth: 155,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
    {
      field: "fee_book_concession_total",
      headerName: "Fee Book Concession Total",
      minWidth: 205,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
    {
      field: "paid_amount",
      headerName: "Paid Amount",
      minWidth: 125,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
    {
      field: "balance",
      headerName: "Balance",
      minWidth: 120,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
    { field: "remarks", headerName: "Remarks", minWidth: 220, flex: 1 },
  ];
  const dateWiseColumns = [
    { field: "concession_date", headerName: "Concession Date", minWidth: 160, flex: 1 },
    { field: "fee_head_name", headerName: "Fee Head Name", minWidth: 200, flex: 1 },
    {
      field: "concession_entries",
      headerName: "Concession List / Entries",
      minWidth: 190,
      type: "number",
    },
    {
      field: "total_amount",
      headerName: "Total Concession Amount",
      minWidth: 210,
      type: "number",
      valueFormatter: (params) => moneyText(params.value),
    },
  ];
  return (
    <Box p={2}>
      <Paper sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h6">Fee Concession Report</Typography>
        </Toolbar>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
          <TextField
            label="From Date"
            type="date"
            size="medium"
            required
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            size="medium"
            required
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Academic Year"
            size="medium"
            value={academicYear}
            onChange={(event) => setAcademicYear(event.target.value)}
            sx={{ minWidth: 165 }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value="">All Academic Years</option>
            {academicYears.map((item) => (
              <option key={item.academic_year} value={item.academic_year}>
                {item.academic_year}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Fee Type / Category"
            size="medium"
            value={feeTypeId}
            onChange={(event) => setFeeTypeId(event.target.value)}
            sx={{ minWidth: 190 }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value="">All Fee Types / Categories</option>
            {feeTypes.map((item) => (
              <option key={item.fee_type_id} value={item.fee_type_id}>
                {item.fee_type_name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Fee Head"
            size="medium"
            value={feeHeadId}
            disabled={!academicYear || !feeTypeId}
            onChange={(event) => setFeeHeadId(event.target.value)}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
          >
            <option value="">All Fee Heads</option>
            {feeHeads.map((item) => (
              <option key={item.fee_head_id} value={item.fee_head_id}>
                {item.fee_head_name}
              </option>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={fetchReport}
            disabled={loading}
            sx={{ color: "#fff" }}
          >
            {loading ? <CircularProgress size={22} /> : "Fetch Report"}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadExcel}
            sx={{
              backgroundColor: "#4caf50",
              color: "#fff",
              "&:hover": { backgroundColor: "#357a38" },
            }}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={downloadPdf}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#9a0007" },
            }}
          >
            Download PDF
          </Button>
        </Box>
        <Typography variant="body2" mb={2}>
          Concession Total: ₹
          {moneyText(rows.reduce((sum, row) => sum + money(row.concession_amount), 0))}
        </Typography>
        <Typography variant="h6" mb={1}>
          Date-wise Concession Summary
        </Typography>
        <Box sx={{ height: 320, width: "100%", mb: 3 }}>
          <DataGrid rows={dateWiseRows} columns={dateWiseColumns} disableRowSelectionOnClick />
        </Box>
        <Typography variant="h6" mb={1}>
          Fee Head-wise Date-by-Date Concession Report
        </Typography>
        {Object.keys(grouped)
          .sort()
          .map((feeHead) => {
            const feeHeadRows = grouped[feeHead];
            const feeHeadTotal = feeHeadRows.reduce(
              (sum, row) => sum + money(row.concession_amount),
              0
            );
            return (
              <Box key={feeHead} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ backgroundColor: "#e3f2fd", px: 2, py: 1, borderRadius: 1 }}
                >
                  Fee Head: {feeHead}
                </Typography>
                <Box
                  sx={{
                    height: Math.min(Math.max(feeHeadRows.length * 52 + 112, 220), 560),
                    width: "100%",
                  }}
                >
                  <DataGrid
                    rows={feeHeadRows}
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter={feeHeadRows.length <= 10}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    pageSizeOptions={[10, 25, 50, 100]}
                  />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  textAlign="right"
                  sx={{ backgroundColor: "#f5f5f5", px: 2, py: 1 }}
                >
                  {feeHead} — Concession Total: ₹{moneyText(feeHeadTotal)}
                </Typography>
              </Box>
            );
          })}
        {!rows.length && (
          <Typography variant="body2" color="text.secondary" py={3} textAlign="center">
            Select the filters and fetch the report to view fee-head-wise student groups.
          </Typography>
        )}
        <Typography variant="body2" fontWeight="bold" mt={1}>
          Detailed Totals — Fee Amount: ₹{moneyText(detailTotals.feeAmount)} | Concession: ₹
          {moneyText(detailTotals.concession)} | Paid: ₹{moneyText(detailTotals.paid)} | Balance: ₹
          {moneyText(detailTotals.balance)}
        </Typography>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
